import {isUndefined, noop} from 'lodash';
import {DateTime} from 'luxon';
import {useEffect, useReducer} from 'react';

import {globalWindow} from '../../libs/shared/core/src/helpers/browser/browserHelpers';
import {useAppSelector} from '../../src/helpers/appReduxHelpers';
import {
  AvailabilityStatus,
  getFindAvailabilityStatus,
} from '../../src/store/data/teammates/availabilityStatusSelectors';

/*
 * Hooks.
 */

const findNow = () => DateTime.now();

export function useAvailabilityStatus(): AvailabilityStatus {
  const findAvailabilityStatus = useAppSelector(getFindAvailabilityStatus);
  const [availabilityStatus, updateAvailabilityStatus] = useReducer(
    (_availabilityStatus: unknown, now: DateTime) => findAvailabilityStatus(now),
    undefined,
    // This is an illegal side-effect, but we prefer the initial render to be correct and we don't care about
    // server-side rendering.
    () => findAvailabilityStatus(findNow()),
  );

  // Recalculate when the lookup function identity changes.
  useEffect(() => {
    updateAvailabilityStatus(findNow());
  }, [findAvailabilityStatus]);

  // Recalculate when the availability status expires.
  const untilTime = availabilityStatus.until?.toMillis();
  useEffect(() => {
    if (isUndefined(untilTime)) {
      return noop;
    }

    return schedule(() => {
      updateAvailabilityStatus(findNow());
    }, untilTime);
  }, [untilTime]);

  return availabilityStatus;
}

/*
 * Helpers.
 */

/** Schedules the callback to be called at a specific time in the future. */
function schedule(callback: () => void, timeInMs: number, pollIntervalInMs = 30000): () => void {
  const remainingInterval = () => timeInMs - Date.now();

  let timeout: ReturnType<typeof globalWindow.setInstrumentedTimeout>;
  const scheduleTimeout = () => {
    // Set a timer that _should_ land us at the requested time. Schedule at least 1 millisecond out, because
    // we don't require that it runs in the next event loop cycle. Schedule at most X seconds out, so we can
    // catch it if the clock jumps forward (due to NTP, for example).
    timeout = globalWindow.setInstrumentedTimeout(
      () => {
        // Keep rescheduling the timer until the availability status expires in case the clock jumped backwards.
        if (remainingInterval() > 0) {
          scheduleTimeout();
          return;
        }

        callback();
      },
      Math.max(1, Math.min(remainingInterval(), pollIntervalInMs)),
    );
  };

  scheduleTimeout();
  return () => globalWindow.clearTimeout(timeout);
}
